import { useTranslation } from 'react-i18next'

import { useNavigate } from 'react-router-dom'

import { MapPin } from 'lucide-react'

import { cn } from '@/lib/utils'

import { useBranches } from '@/hooks/useBranches'
import type { Branch } from '@/hooks/useBranches'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import ErrorMessage from '@/components/errors/ErrorMessage'

import { SmartButton } from '@/components/ui-custom/SmartButton'

import { paths } from '../../config/paths'

interface BranchSelectorProps {
  selectedBranch: string | null
  onNext: () => void
  onSelect: (branchId: string) => void
}

interface BranchCardProps {
  branch: Branch
  isSelected: boolean
  onSelect: (id: string) => void
}

export function BranchSelector({ selectedBranch, onNext, onSelect }: BranchSelectorProps) {
  const { t } = useTranslation(['auth', 'common'])
  const navigate = useNavigate()
  const { data: branches, isLoading, error } = useBranches()

  if (isLoading) {
    return (
      <div className='mx-auto min-h-[500px] max-w-3xl space-y-8'>
        <Card className='rounded-xl border-0 px-6 py-8 shadow-lg'>
          <CardHeader className='pb-6'>
            <div className='mx-auto h-8 w-1/2 animate-pulse rounded bg-gray-200'></div>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {[...Array(3)].map((_, i) => (
                <div key={i} className='h-20 animate-pulse rounded-xl bg-gray-100'></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : t('auth:selectBranchForm.error', {
            defaultValue: 'Failed to load branch data',
          })

    return <ErrorMessage message={errorMessage} />
  }

  if (!branches || branches.length === 0) {
    return (
      <div className='mx-auto max-w-3xl space-y-8'>
        <Card className='rounded-xl border-0 px-6 py-8 shadow-lg'>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <div className='bg-muted mb-4 flex h-16 w-16 items-center justify-center rounded-full'>
              <MapPin className='text-muted-foreground h-8 w-8' />
            </div>
            <h3 className='text-foreground mb-2 text-xl font-semibold'>
              {t('auth:selectBranchForm.empty.title', 'No Branches Available')}
            </h3>
            <p className='text-muted-foreground mb-6 text-center text-sm'>
              {t(
                'auth:selectBranchForm.empty.description',
                'There are no branches available at the moment. Please contact support for assistance.'
              )}
            </p>
            <SmartButton variant='outline' onClick={() => navigate(paths.public.auth.login.getHref())} className='mt-4'>
              {t('common:actions.backToLoginLink', 'Back To Login')}
            </SmartButton>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='mx-auto max-w-3xl space-y-8'>
      <Card className='rounded-xl border-0 px-6 py-8 shadow-lg'>
        <CardHeader className='pb-6'>
          <CardTitle className='text-primary text-center text-2xl font-bold'>
            {t('auth:selectBranchForm.title', {
              defaultValue: 'Select Preferred Branch',
            })}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <div className='border-muted scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent max-h-[400px] space-y-3 overflow-y-auto rounded-lg border p-2'>
            {branches?.map((branch) => (
              <BranchCard
                key={branch.id}
                branch={branch}
                isSelected={branch.id === selectedBranch}
                onSelect={onSelect}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className='flex flex-col items-center gap-4'>
        <SmartButton
          variant={'default'}
          onClick={onNext}
          disabled={!selectedBranch}
          size='lg'
          className={cn(
            'h-12 w-full text-base font-semibold sm:w-64',
            !selectedBranch ? 'cursor-not-allowed bg-gray-300 text-gray-600' : null
          )}
        >
          {t('auth:selectBranchForm.submit', {
            defaultValue: 'Continue',
          })}
        </SmartButton>
        <SmartButton
          variant={'link'}
          onClick={() => navigate(paths.public.auth.login.getHref())}
          type='button'
          className='text-sm text-gray-600 transition-all hover:text-gray-800'
        >
          {t('common:actions.backToLoginLink', 'Back To Login')}
        </SmartButton>
      </div>
    </div>
  )
}

function BranchCard({ branch, isSelected, onSelect }: BranchCardProps) {
  const { t } = useTranslation(['auth'])

  return (
    <Card
      onClick={() => onSelect(branch.id)}
      className={cn(
        'cursor-pointer rounded-xl border-2 p-5 transition-all hover:shadow-md',
        isSelected
          ? 'border-primary ring-primary/10 bg-primary/5 shadow-md ring-2'
          : 'border-muted hover:border-primary/30'
      )}
    >
      <CardContent className='space-y-3 p-0'>
        {/* Header row */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                isSelected ? 'bg-primary/10 text-primary/80' : 'bg-muted text-muted-foreground'
              )}
            >
              <MapPin className='h-5 w-5' />
            </div>
            <div>
              <h3 className='text-foreground text-lg font-semibold'>{branch.name}</h3>
              <p className='text-muted-foreground text-sm'>{branch.address}</p>
            </div>
          </div>

          {isSelected && (
            <span className='inline-flex items-center rounded-full border border-blue-200 bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800'>
              {t('auth:selectBranchForm.badge.state.selected', {
                defaultValue: 'Selected',
              })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
